<script setup>
import { ref } from 'vue'

const mobileOpen = ref(false)

const navLinks = [
  { to: '/soluciones', label: 'Soluciones' },
  { to: '/industrias', label: 'Industrias' },
  { to: '/tecnologia', label: 'Tecnología' },
  { to: '/empresa',   label: 'Empresa' },
  { to: '/contacto',  label: 'Contacto' },
]

function closeMobile() {
  mobileOpen.value = false
}
</script>

<template>
  <header class="header">
    <div class="container header__inner">
      <!-- Logo -->
      <RouterLink to="/" class="header__logo" @click="closeMobile" aria-label="Brixel Technologies — Inicio">
        <img src="@/assets/logo/Brixel_logo_H1.svg" alt="Brixel Technologies" class="header__logo-img" />
      </RouterLink>

      <!-- Navegación desktop -->
      <nav class="header__nav" aria-label="Navegación principal">
        <RouterLink
          v-for="link in navLinks"
          :key="link.to"
          :to="link.to"
          class="header__link"
          :active-class="'header__link--active'"
        >
          {{ link.label }}
        </RouterLink>
        <RouterLink to="/contacto" class="btn btn--primary header__cta">
          Agendar diagnóstico
        </RouterLink>
      </nav>

      <!-- Hamburguesa mobile -->
      <button
        class="header__hamburger"
        :class="{ 'header__hamburger--open': mobileOpen }"
        :aria-expanded="mobileOpen"
        aria-label="Abrir menú"
        @click="mobileOpen = !mobileOpen"
      >
        <span /><span /><span />
      </button>
    </div>

    <!-- Menú mobile -->
    <div class="header__mobile" :class="{ 'header__mobile--open': mobileOpen }" role="navigation">
      <RouterLink
        v-for="link in navLinks"
        :key="link.to"
        :to="link.to"
        class="header__mobile-link"
        @click="closeMobile"
      >
        {{ link.label }}
      </RouterLink>
      <RouterLink to="/contacto" class="btn btn--primary header__mobile-cta" @click="closeMobile">
        Agendar diagnóstico
      </RouterLink>
    </div>
  </header>
</template>

<style scoped lang="scss">
@import '@/styles/variables';

.header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid $color-border;
  height: $header-height;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);

  &__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

  &__logo {
    display: flex;
    align-items: center;
    text-decoration: none;
    flex-shrink: 0;
  }

  &__logo-img {
    height: 28px;
    width: auto;
    display: block;
  }

  &__nav {
    display: flex;
    align-items: center;
    gap: $space-6;

    @media (max-width: $bp-lg) {
      display: none;
    }
  }

  &__link {
    font-size: $text-sm;
    font-weight: 500;
    color: $color-text-secondary;
    text-decoration: none;
    transition: color $transition-fast;
    position: relative;
    white-space: nowrap;

    &::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 0;
      height: 2px;
      background: $color-secondary;
      transition: width $transition-base;
    }

    &:hover {
      color: $color-text;
      &::after { width: 100%; }
    }

    &--active {
      color: $color-primary;
      font-weight: 600;
      &::after { width: 100%; background: $color-primary; }
    }
  }

  &__cta {
    padding: $space-2 $space-4;
    font-size: $text-sm;
    border-radius: $radius-md;
    white-space: nowrap;
  }

  // ── Hamburguesa ──────────────────────────────────────────────
  &__hamburger {
    display: none;
    flex-direction: column;
    gap: 5px;
    padding: $space-2;
    background: none;
    border: none;
    cursor: pointer;

    @media (max-width: $bp-lg) {
      display: flex;
    }

    span {
      display: block;
      width: 24px;
      height: 2px;
      background: $color-text;
      border-radius: $radius-full;
      transition: transform $transition-base, opacity $transition-base;
    }

    &--open {
      span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
      span:nth-child(2) { opacity: 0; }
      span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }
    }
  }

  // ── Menú mobile ──────────────────────────────────────────────
  &__mobile {
    display: none;
    flex-direction: column;
    background: #fff;
    border-top: 1px solid $color-border;

    @media (max-width: $bp-lg) {
      display: flex;
      max-height: 0;
      overflow: hidden;
      transition: max-height $transition-slow, padding $transition-slow;
      padding: 0 $space-6;

      &--open {
        max-height: 480px;
        padding: $space-4 $space-6 $space-6;
      }
    }

    &-link {
      display: block;
      padding: $space-3 0;
      font-size: $text-base;
      font-weight: 500;
      color: $color-text;
      text-decoration: none;
      border-bottom: 1px solid $color-border;

      &:hover { color: $color-primary; }
    }

    &-cta {
      margin-top: $space-4;
      text-align: center;
    }
  }
}
</style>
